import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Search, Filter, Download, Mail, UserMinus } from 'lucide-react';
import { fetchSubscribers, unsubscribeEmail } from '../../redux/features/subscriptionSlice';

const SubscriberListPage = () => {
    const dispatch = useDispatch();
    const { subscribers, status, error } = useSelector(state => state.subscription);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('active');

    useEffect(() => {
        dispatch(fetchSubscribers());
    }, [dispatch]);

    const handleUnsubscribe = async (email) => {
        if (window.confirm('Are you sure you want to unsubscribe this email?')) {
            dispatch(unsubscribeEmail(email));
        }
    };
    const handleExportCSV = () => {
        const csvContent = [
            ['Email', 'Status', 'Subscribed Date'],
            ...filteredSubscribers.map(sub => [
                sub.email,
                sub.isActive ? 'Active' : 'Inactive',
                new Date(sub.createdAt.seconds * 1000).toLocaleDateString()
            ])
        ].map(row => row.join(',')).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `subscribers-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const filteredSubscribers = subscribers
        .filter(sub => {
            // Add null check for email
            if (!sub || !sub.email) {
                console.warn('Invalid subscriber data:', sub);
                return false;
            }
            return sub.email?.toLowerCase().includes(searchTerm?.toLowerCase());
        })
        .filter(sub => {
            if (filterStatus === 'all') return true;
            // Add null check for isActive
            if (sub.isActive === undefined) {
                console.warn('isActive undefined for subscriber:', sub);
                return false;
            }
            return filterStatus === 'active' ? sub.isActive : !sub.isActive;
        });

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white px-6 py-4 rounded-lg shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <h2 className="text-2xl font-bold text-gray-900">
                        Subscriber Management
                    </h2>
                    <button
                        onClick={handleExportCSV}
                        className="inline-flex items-center justify-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors gap-2"
                    >
                        <Download className="w-4 h-4" />
                        <span>Export CSV</span>
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white p-6 rounded-lg shadow-sm">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="Search by email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 w-full rounded-lg border-gray-200 focus:border-indigo-500 focus:ring-indigo-500"
                        />
                    </div>
                    <div className="relative">
                        <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="pl-10 w-full rounded-lg border-gray-200 focus:border-indigo-500 focus:ring-indigo-500"
                        >
                            <option value="all">All Subscribers</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Subscribers List */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Email
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Subscribed Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredSubscribers.map(subscriber => (
                                <tr key={subscriber.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">{subscriber.email}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-500">
                                            {new Date(subscriber.createdAt.seconds * 1000).toLocaleDateString()}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${subscriber.isActive
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-red-100 text-red-800'
                                            }`}>
                                            {subscriber.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex items-center justify-end space-x-3">
                                            <a
                                                href={`mailto:${subscriber.email}`}
                                                className="text-gray-600 hover:text-indigo-600 transition-colors"
                                                title="Send Email"
                                            >
                                                <Mail className="w-5 h-5" />
                                            </a>
                                            {subscriber.isActive && (
                                                <button
                                                    onClick={() => handleUnsubscribe(subscriber.email)}
                                                    className="text-gray-600 hover:text-red-600 transition-colors"
                                                    title="Unsubscribe"
                                                >
                                                    <UserMinus className="w-5 h-5" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Status Messages */}
                {status === 'loading' && (
                    <div className="flex justify-center items-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    </div>
                )}
                {error && (
                    <div className="flex justify-center py-8">
                        <p className="text-red-500 flex items-center gap-2">
                            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                            {error}
                        </p>
                    </div>
                )}
                {status !== 'loading' && filteredSubscribers.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-12">
                        <p className="text-gray-500 text-lg">No subscribers found</p>
                        <p className="text-gray-400 text-sm mt-2">Try adjusting your search or filters</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubscriberListPage;